import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { RxCross2 } from "react-icons/rx";
import { ToastContainer } from "react-toastify";

export default function CreateHomeService() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: "",
        image: "",
        pricingOptions: []
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const addPricingOption = () => {
        setFormData(prev => ({
            ...prev,
            pricingOptions: [
                ...prev.pricingOptions,
                { title: "", price: "", image: "", description: "" }
            ]
        }));
    };

    const removePricingOption = (index) => {
        setFormData(prev => ({
            ...prev,
            pricingOptions: prev.pricingOptions.filter((_, i) => i !== index)
        }));
    };

    const handlePricingOptionChange = (index, field, value) => {
        const updatedOptions = [...formData.pricingOptions];
        updatedOptions[index][field] = value;
        setFormData(prev => ({ ...prev, pricingOptions: updatedOptions }));
    };

    const handleOptionImageChange = async (index, e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const res = await uploadFiles({
                fieldName: "file",
                files: [file],
            });

            if (res?.error) {
                return showErrorToast(res.message || "Failed to upload image!");
            }

            handlePricingOptionChange(index, "image", res.data.imageUrl);
        } catch (error) {
            showErrorToast(error.message || "Image upload error");
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const res = await uploadFiles({
                fieldName: "file",
                files: [file],
            });

            if (res?.error) {
                return showErrorToast(res.message || "Failed to upload image!");
            }

            setFormData((prev) => ({
                ...prev,
                image: res.data.imageUrl,
            }));
        } catch (error) {
            showErrorToast(error.message || "Image upload error");
        }
    };

    const removeImage = () => {
        setFormData((prev) => ({
            ...prev,
            image: "",
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                name: formData.name,
                image: formData.image,
                pricingOptions: formData.pricingOptions
            };

            const res = await makeRequestAuth(
                "post",
                "/admin/saveServiceCategory",
                payload
            );

            if (res?.error) {
                return showErrorToast(res.message || "Failed to create service");
            }

            showSuccessToast("Service created successfully!");
            setFormData({
                image: '',
                name: '',
                pricingOptions: []
            })

        } catch (error) {
            console.error("Create service error:", error);
            showErrorToast(
                error?.message || "Something went wrong!"
            );
        }
    };

    return (
        <div className="p-6 max-w-4xl">
            <ToastContainer />
            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-[#434646]">
                    Create Home Service
                </h2>

                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 border rounded-xl"
                >
                    Back
                </button>
            </div>

            {/* FORM */}
            <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl shadow p-6 space-y-6"
            >
                {/* SERVICE NAME */}
                <div>
                    <label className="block mb-2 font-medium">
                        Service Name
                    </label>
                    <input
                        type="text"
                        name="name"
                        required
                        placeholder="Enter service name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full border px-4 py-2 rounded-xl"
                    />
                </div>

                {/* IMAGE UPLOAD + PREVIEW */}
                <div>
                    <label className="block mb-2 font-medium">
                        Service Image
                    </label>

                    {!formData.image ? (
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full border px-4 py-2 rounded-xl"
                        />
                    ) : (
                        <div className="relative w-[200px] h-[140px]">
                            <img
                                src={formData.image}
                                alt="Service Preview"
                                className="w-full h-full object-cover rounded-xl border"
                            />

                            {/* REMOVE ICON */}
                            <button
                                type="button"
                                onClick={removeImage}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                            >
                                <RxCross2 size={14} />
                            </button>
                        </div>
                    )}
                </div>

                <hr />

                {/* PRICING OPTIONS SECTION */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Pricing Options</h3>
                        <button
                            type="button"
                            onClick={addPricingOption}
                            className="px-4 py-1 bg-green-500 text-white rounded-lg text-sm"
                        >
                            + Add Option
                        </button>
                    </div>

                    <div className="space-y-4">
                        {formData.pricingOptions.map((option, index) => (
                            <div key={index} className="border p-4 rounded-xl relative bg-gray-50">
                                <button
                                    type="button"
                                    onClick={() => removePricingOption(index)}
                                    className="absolute top-2 right-2 text-red-500"
                                >
                                    <RxCross2 size={20} />
                                </button>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm mb-1">Title</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Basic Service"
                                            value={option.title}
                                            onChange={(e) => handlePricingOptionChange(index, "title", e.target.value)}
                                            className="w-full border px-3 py-1.5 rounded-lg text-sm"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">Price</label>
                                        <input
                                            type="number"
                                            placeholder="Price"
                                            value={option.price}
                                            onChange={(e) => handlePricingOptionChange(index, "price", e.target.value)}
                                            className="w-full border px-3 py-1.5 rounded-lg text-sm"
                                            required
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm mb-1">Description</label>
                                        <textarea
                                            placeholder="Description"
                                            value={option.description}
                                            onChange={(e) => handlePricingOptionChange(index, "description", e.target.value)}
                                            className="w-full border px-3 py-1.5 rounded-lg text-sm h-20"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm mb-1">Option Image</label>
                                        {option.image ? (
                                            <div className="relative w-[120px] h-[80px]">
                                                <img src={option.image} className="w-full h-full object-cover rounded-lg border" />
                                                <button
                                                    type="button"
                                                    onClick={() => handlePricingOptionChange(index, "image", "")}
                                                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                                                >
                                                    <RxCross2 size={12} />
                                                </button>
                                            </div>
                                        ) : (
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleOptionImageChange(index, e)}
                                                className="w-full border px-3 py-1.5 rounded-lg text-sm"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {formData.pricingOptions.length === 0 && (
                            <p className="text-gray-400 text-sm italic">No pricing options added yet.</p>
                        )}
                    </div>
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex gap-4 pt-4">
                    <button
                        type="submit"
                        className="px-6 py-2 bg-[#392DFF] text-white rounded-xl"
                    >
                        Create Service
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate("/admin/home-services")}
                        className="px-6 py-2 border rounded-xl"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}
